import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

type ViewportSize = "mobile" | "tablet" | "desktop";

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}) => {
  const { t } = useTranslation("common");
  const [viewportSize, setViewportSize] = useState<ViewportSize>("desktop");

  useEffect(() => {
    const updateViewportSize = () => {
      if (window.innerWidth < 480) {
        setViewportSize("mobile");
      } else if (window.innerWidth < 768) {
        setViewportSize("tablet");
      } else {
        setViewportSize("desktop");
      }
    };
    updateViewportSize();
    window.addEventListener("resize", updateViewportSize);
    return () => window.removeEventListener("resize", updateViewportSize);
  }, []);

  if (isLoading) {
    return (
      <div className="pagination-container pagination-skeleton">
        <Skeleton height={36} width={250} />
      </div>
    );
  }

  if (totalPages <= 1) return null;

  const getMaxPagesToShow = (): number => {
    switch (viewportSize) {
      case "mobile":
        return 1;
      case "tablet":
        return 3;
      default:
        return 5;
    }
  };

  const maxPagesToShow = getMaxPagesToShow();
  const pages: number[] = [];
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = startPage + maxPagesToShow - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handleClick = (
    page: number,
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
      window.scrollTo(0, 0);
    }
  };

  if (viewportSize === "mobile") {
    return (
      <div className="pagination-container pagination-mobile">
        <ul className="pagination">
          {currentPage > 1 && (
            <li>
              <a
                href="#"
                onClick={(e) => handleClick(currentPage - 1, e)}
                className="prev-page"
                aria-label={t("productPagination.previous")}
              >
                «
              </a>
            </li>
          )}
          <li className="current-page-indicator">
            <span>
              {t("productPagination.pageOf", { currentPage, totalPages })}
            </span>
          </li>
          {currentPage < totalPages && (
            <li>
              <a
                href="#"
                onClick={(e) => handleClick(currentPage + 1, e)}
                className="next-page"
                aria-label={t("productPagination.next")}
              >
                »
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className={`pagination-container pagination-${viewportSize}`}>
      <ul className="pagination">
        {currentPage > 1 && (
          <li>
            <a
              href="#"
              onClick={(e) => handleClick(currentPage - 1, e)}
              className="prev-page"
              aria-label={t("productPagination.previous")}
            >
              «
              {viewportSize !== "tablet" &&
                ` ${t("productPagination.previous")}`}
            </a>
          </li>
        )}
        {startPage > 1 && (
          <>
            <li>
              <a href="#" onClick={(e) => handleClick(1, e)}>
                1
              </a>
            </li>
            {startPage > 2 && <li className="ellipsis">...</li>}
          </>
        )}
        {pages.map((page) => (
          <li key={page}>
            <a
              href="#"
              onClick={(e) => handleClick(page, e)}
              className={page === currentPage ? "current" : ""}
            >
              {page}
            </a>
          </li>
        ))}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <li className="ellipsis">...</li>}
            <li>
              <a href="#" onClick={(e) => handleClick(totalPages, e)}>
                {totalPages}
              </a>
            </li>
          </>
        )}
        {currentPage < totalPages && (
          <li>
            <a
              href="#"
              onClick={(e) => handleClick(currentPage + 1, e)}
              className="next-page"
              aria-label={t("productPagination.next")}
            >
              {viewportSize !== "tablet" && `${t("productPagination.next")} `}»
            </a>
          </li>
        )}
      </ul>
    </div>
  );
};

export default Pagination;
